
import type { LanguageOption, Translations } from './types';

export const LANGUAGES: LanguageOption[] = [
  { code: 'sv', name: 'Svenska' },
  { code: 'en', name: 'English' },
  { code: 'da', name: 'Dansk' },
];

export const NATIONALITIES = [
  { code: 'SE', name: 'Sweden' },
  { code: 'DK', name: 'Denmark' },
  { code: 'NO', name: 'Norway' },
  { code: 'FI', name: 'Finland' },
  { code: 'DE', name: 'Germany' },
  { code: 'GB', name: 'United Kingdom' },
];

export const CURRENCIES = [
  { code: 'SEK', name: 'Swedish Krona (kr)' },
  { code: 'DKK', name: 'Dansk Krone (kr)' },
];

export const NIGHTS = Array.from({ length: 30 }, (_, i) => i + 1);

export const TRANSLATIONS: Translations = {
  en: {
    // Header
    holidays: 'Holidays',
    hotels: 'Hotels',
    flights: 'Flights',

    // Search Widget
    package_tours: 'Package Tours',
    only_hotel: 'Only Hotel',
    transfer: 'Transfer',
    departure: 'Departure',
    destination: 'Destination',
    check_in_date: 'Check In',
    night: 'Nights',
    nationality: 'Nationality',
    currency: 'Currency',
    room_and_person_number: 'Room And Person Number',
    advance_search: 'Advance Search',
    choose: 'Choose',
    guests: 'Guests',
    adults: 'Adults',
    children: 'Children',
    child_ages: 'Child Ages',
    child_age: 'Child {0} Age',
    search: 'Search',

    // Hero & Landing Page
    hero_badge: 'Explore the World',
    hero_title_1: 'Discover Your',
    hero_title_2: 'Perfect Getaway',
    hero_subtitle: 'Here you can search and book unforgettable trips with Lumi Tours – we create our own carefully selected experiences, dedicated to giving you a fantastic journey from start to finish. Explore our exclusive packages with luxury hotels, flights, and memorable moments, designed entirely by us.',

    feature_fast_title: 'Fast & Easy Booking',
    feature_fast_desc: 'Book your dream holiday in just a few clicks. Instant confirmation and secure payments.',
    feature_curated_title: 'Handpicked Experiences',
    feature_curated_desc: 'At Lumi Tours, we scout and handpick the best destinations and hotels – carefully selected just for you. Every trip is curated, personal, and created to give you an unforgettable experience from the very first moment.',
    feature_support_title: '24/7 Support',
    feature_support_desc: 'Our dedicated travel experts are here to help you before, during, and after your trip.',

    inspiration: 'Inspiration',
    popular_destinations: 'Popular Destinations',
    popular_subtitle: 'Explore our most visited locations this season and find your next adventure.',

    // CTA Section
    cta_badge: 'Early Booking Offer',
    cta_title: 'Escape to Blue Dreams Resort',
    cta_desc: 'Plan your dream getaway now and save up to 25% on your stay. Enjoy pristine beaches, luxury amenities, and unforgettable moments at this top-rated Bodrum resort.',
    cta_button: 'Discover The Offer',

    // Campaign Banner
    campaign_banner_text: "✨ FLASH SALE! Up to 30% off on all Turkey destinations this week! ✨",

    // Membership / Privé Section
    member_title: 'Unlock Lumi Privé Access',
    member_desc: 'Join our inner circle of travelers. Members receive secret pricing, complimentary room upgrades, and priority access to our curated collections.',
    benefit_deals: 'Secret Member Rates',
    benefit_support: 'Priority Concierge',
    benefit_curated: 'Handpicked Gems',
    join_now: 'Unlock Benefits',
    enter_email: 'Enter your email address',

    get_exclusive_deals: 'Access Curated Journeys',
    newsletter_desc: 'Join our newsletter to receive exclusive, handpicked travel experiences shared only with our members.',
    subscribe: 'Subscribe',

    // Holidays Page
    holidays_hero_title: 'Unforgettable Journeys',
    holidays_hero_subtitle: 'From sun-soaked beaches to cultural city breaks, find the complete holiday package that suits your style.',
    our_services: 'Our Premium Services',
    service_flights_title: 'Global Flights',
    service_flights_desc: 'Access to major airlines and exclusive charter flights to top destinations worldwide.',
    service_hotels_title: 'Handpicked Hotels',
    service_hotels_desc: 'From boutique stays to 5-star all-inclusive resorts, we curate the best for you.',
    service_transfers_title: 'Seamless Transfers',
    service_transfers_desc: 'Private and shared airport transfers ensuring a smooth start to your vacation.',
    trending_packages: 'Trending Packages',
    view_package: 'View Package',
    why_choose_us: 'Why Choose LumiTours?',
    why_desc_1: 'Expertly crafted itineraries designed for the modern traveler.',
    why_desc_2: 'Full financial protection and secure booking technology.',
    why_desc_3: 'Local partnerships ensuring authentic experiences.',

    // Landing Page Sub-pages
    landing_holidays_title: 'Unforgettable Holidays',
    landing_holidays_subtitle: 'Sun, sea, and sand. Discover the world\'s best package tours.',
    landing_holidays_badge: 'All Inclusive Deals',
    landing_hotels_title: 'Find Your Perfect Stay',
    landing_hotels_subtitle: 'From cozy boutique hotels to 5-star luxury resorts.',
    landing_hotels_badge: 'Premium Stays',
    landing_flights_title: 'Charter Flights & Packages',
    landing_flights_subtitle: 'Direct flights to popular holiday destinations.',
    landing_flights_badge: 'Fly High',
    landing_last_minute_title: 'Last Minute Deals',
    landing_last_minute_desc: 'Grab a bargain on departures within the next 4 weeks.',
    landing_best_price_title: 'Best Price Guarantee',
    landing_best_price_desc: 'We compare prices across hundreds of providers.',
    landing_secure_booking_title: 'Secure Booking',
    landing_secure_booking_desc: 'Your payments and data are always protected.',

    // Flights Page
    flights_hero_title: 'Find Your Wings',
    flights_hero_subtitle: 'Search and compare cheap flights from all major airlines. Your next journey starts here.',
    top_flight_destinations: 'Top Flight Destinations',
    top_flight_subtitle: 'Discover our most popular routes and book your dream ticket today.',
    one_way: 'One Way',
    round_trip: 'Round Trip',
    departure_airport: 'Departure Airport',
    arrival_airport: 'Arrival Airport',
    departure_date: 'Departure Date',
    return_date: 'Return Date',
    passengers: 'Passengers',
    infants: 'Infants',

    // Search Results
    search_again: 'Search Again',
    search_filters: 'SEARCH FILTERS',
    reset_all: 'Reset All',
    hotel_search: 'Hotel Search',
    hotel_price: 'Hotel Price',
    hotel_stars: "Hotel Star's",
    hotel_boards: 'Hotel Boards',
    hotel_locations: 'Hotel Locations',
    hotel_themes: 'Hotel Themes',
    hotel_facilities: 'Hotel Facilities',
    options: 'OPTIONS',
    matches_your_search_criteria: 'Matches your search criteria',
    sort_by: 'Sort by',
    compare: 'Compare',
    show_on_map: 'Show on Map',
    social_media_share: 'Social Media Share',
    hotel_description: 'Hotel Description',
    included: 'Included',
    room_price_starting_from: 'Room Price Starting From',
    filters: 'Filters',
    price_range: 'Price Range',
    star_rating: 'Star Rating',
    all: 'All',
    view_deal: 'View Deal',
    book_now: 'Book Now',
    per_night: 'per night',
    total_price: 'Total Price',
    per_person: 'per person',
    no_results: 'No results found. Please try a different search.',
    back_to_search: 'Back to Search',
    searching_for_deals: 'Searching for the best deals...',

    // Booking & Payment
    passenger_details: 'Passenger Details',
    contact_details: 'Contact Details',
    payment_details: 'Payment Details',
    first_name: 'First Name',
    last_name: 'Last Name',
    birth_date: 'Birth Date',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    zip_code: 'Zip Code',
    country: 'Country',
    card_number: 'Card Number',
    card_holder: 'Card Holder Name',
    expiry_date: 'Expiry Date',
    cvv: 'CVV/CVC',
    pay_now: 'Pay & Confirm',
    booking_summary: 'Booking Summary',
    check_in: 'Check-in',
    check_out: 'Check-out',
    nights: 'Nights',
    room_type: 'Room Type',
    board_basis: 'Board Basis',
    total_amount: 'Total Amount',
    secure_payment: 'Secure Payment',
    reservation_success: 'Reservation Successful!',
    reservation_number: 'Reservation Number',
    thank_you_message: 'Thank you for your booking. A confirmation email has been sent to you.',
    back_to_home: 'Back to Home',
    processing: 'Processing...',
    adult: 'Adult',
    child: 'Child',
    passenger: 'Passenger',
    booking_overview: 'Booking Overview',
    complete_booking_msg: 'Complete your details to finalize the reservation',
    back_to_hotel: 'Back to Hotel',
    lead_passenger: 'Lead Passenger',
    proceed_to_payment: 'Proceed to Payment →',
    price_change: 'Price Change',
    price_breakdown: 'Price Breakdown',
    all_taxes_included: 'All taxes included',
    your_trip: 'Your Trip',
    booking_valid_for: 'Booking valid for',
    total_amount_label: 'total amount',
    adults_short: 'adults',
    children_short: 'children',
    room: 'room',
    flight: 'Flight',
    share_trip: 'Share trip',
    link_copied: 'Link copied!',
    flight_change: 'flight change',
    show_full_flight_info: 'Show full flight information',
    start_new_session: 'Start new session',
    baggage_selection: 'Baggage Selection',
    extra_baggage: 'Extra Baggage',

    // Payment Page
    payment_initializing: 'Initializing Payment Gateways...',
    payment_connecting: 'Connecting to reservation system...',
    payment_fetching_methods: 'Fetching payment methods...',
    payment_confirming: 'Confirming booking...',
    payment_processing: 'Processing payment...',
    payment_redirecting: 'Redirecting to payment gateway...',
    payment_redirecting_short: 'Redirecting...',
    payment_connection_issue: 'Connection Issue',
    payment_retry: 'Retry',
    payment_select_method: 'Select Payment Method',
    payment_no_methods: 'No payment methods found. Please try again later.',
    payment_redirect_info: 'You will be redirected to our secure payment partner to complete your transaction safely.',
    payment_accept_terms_prefix: 'I accept the',
    payment_terms_title: 'Terms and Conditions',
    payment_accept_close: 'Accept & Close',
    payment_proceed: 'Proceed to Secure Payment',

    // TravelMate
    view_hotel: 'View Hotel',
    travelmate_ai: 'Lumi Assist',
    beta: 'Beta',
    your_budget: 'Your Budget',
    gap: 'Gap',
    market_avg: 'Market Avg',
    est_total: 'Est. Total',

    // Chat / Live Support
    chat_support: "Live Support",
    chat_ai_assistant: "AI Assistant",
    chat_agent_active: "Agent Active",
    chat_waiting_agent: "Waiting for Agent...",
    chat_request_agent: "Need human help? Request Agent",
    chat_type_message: "Type a message...",
    chat_listening: "Listening...",
    chat_welcome: "Hello! I am Luna, your virtual travel assistant. I can check prices, help you book, or answer questions. How can I help you today?",
    chat_agent_connected: "Connecting you to a human agent...",
    chat_agent_notified: "I have notified a human agent. They will be with you shortly. Estimated wait time: 2 minutes.",

    // Footer
    copyright: '© 2025 Lumitours. All rights reserved.',
    admin_login: 'Admin',

    // Admin
    dashboard: 'Dashboard',
    bookings: 'Bookings',
    my_bookings: 'My Bookings',
    manage_reservations: 'Manage your reservations and documents',
    content_manager: 'Content Manager',
    logs: 'System Logs',
    settings: 'API Settings',
    logout: 'Logout',
    admin_panel: 'Admin Panel',
    recent_bookings: 'Recent Bookings',
    total_revenue: 'Total Revenue',
    active_users: 'Active Users',
    system_status: 'System Status',
    inspect_product: 'Inspect Product',
    enter_product_id: 'Enter Product ID (e.g. 8007)',
    fetch_details: 'Fetch Details',
    raw_data: 'Raw Data',
    ai_designer: 'AI Designer',
    live_support: 'Live Support',
    exit_to_app: 'Exit to App',

    // Settings
    api_settings: 'API Settings',
    service_address: 'Service Address',
    agency: 'Agency',
    api_user: 'User',
    api_password: 'Password',
    save_settings: 'Save Settings',
    cancel: 'Cancel',
    settings_saved: 'Settings saved!',
    api_settings_not_configured: 'API settings are not configured. Please configure them first.',
    api_search_error: 'An error occurred while searching. Please check the console for details.',
    test_connection: 'Test Connection',
    api_test_tool: 'API Test Tool',
    test_instructions: 'Click the button to send a test request to the configured service address with the saved credentials.',
    testing: 'Testing...',
    test_success: 'Success! The API responded.',
    test_failed: 'Failed. See response/error below.',
    cors_error_help: 'A network error occurred. This is often a CORS issue. Ensure the server at the service address allows requests from this origin.',
    test_response: 'API Response',

    // API Logs
    api_logs: 'API Logs',
    request: 'Request',
    response: 'Response',

    // Flight Options Modal
    flight_options_title: 'Flight Options',
    flight_options_subtitle: 'View and select alternative flights',
    outbound_flight: 'Outbound',
    inbound_flight: 'Return',
    all_airlines: 'All Airlines',
    direct_flight: 'Direct Flight',
    max_1_stop: 'Max 1 Stop',
    max_2_stops: 'Max 2 Stops',
    loading_flights: 'Loading flights...',
    no_flights_found_criteria: 'No flights found matching these criteria.',
    total_difference: 'Total Difference',
    confirm_flights: 'Confirm Flights',
    selected: 'Selected',
    stops: 'Stops',
    any: 'Any',
    clear_filters: 'Clear Filters',
    tour_details: 'Tour Details',
    alternative_flights: 'Alternative Flights',
    select_this_flight: 'Select This Flight',
    searching_alternative_flights: 'Searching for alternative flights...',
    return_flight: 'Return',
    select_room: 'Select Room',
    total_price_label: 'Total Price',
    view_flight_options: 'View Flight Options',
    no_flights_found: 'No flights found',
    close: 'Close',
    confirm: 'Confirm',
    error_loading_flights: 'An error occurred while loading flight information',

    // Admin Sub-components
    hotel_manager: 'Hotel Manager',
    tour_cache: 'Tour Cache',
    data_explorer: 'Data Explorer',
    page_builder: 'Page Builder',
    field_mapper: 'Field Mapper',
    cms_manager: 'CMS Manager',

    // Common Admin Actions
    add_new: 'Add New',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    refresh: 'Refresh',
    loading: 'Loading...',
    no_data: 'No data available',
    actions: 'Actions',
    status: 'Status',
    name: 'Name',
    description: 'Description',
    created: 'Created',
    updated: 'Updated',
    active: 'Active',
    inactive: 'Inactive',
    confirm_delete: 'Are you sure you want to delete this item?',

    // Hotel Manager
    hotels_list: 'Hotels List',
    add_hotel: 'Add Hotel',
    edit_hotel: 'Edit Hotel',
    hotel_name: 'Hotel Name',
    hotel_location: 'Location',
    hotel_rating: 'Rating',
    hotel_featured: 'Featured',
    sync_hotels: 'Sync Hotels',

    // Tour Cache
    tour_cache_manager: 'Tour Cache Manager',
    queries: 'Queries',
    data: 'Data',
    statistics: 'Statistics',
    backups: 'Backups',
    new_query: 'New Query',
    query_name: 'Query Name',
    departure_point: 'Departure Point',
    arrival_point: 'Arrival Point',
    check_in_start: 'Check-in Start',
    check_in_end: 'Check-in End',
    cache_days: 'Cache Days',
    schedule: 'Schedule',
    automatic_schedule: 'Automatic Schedule',
    work_hour: 'Work Hour',
    save_query: 'Save Query',
    saving: 'Saving...',
    cancel_edit: 'Cancel',
    no_queries: 'No queries found.',
    run: 'Run',
    delete_query_confirm: 'Are you sure you want to delete this query?',
    total_queries: 'Total Queries',
    scheduled: 'Scheduled',
    stop: 'Stop',
    cache_queries: 'Cache Queries',
    add_query: 'Add Query',
    run_query: 'Run Query',
    cache_status: 'Cache Status',
    last_run: 'Last Run',
    total_results: 'Total Results',

    // Data Explorer
    select_entity: 'Select Entity',
    view_details: 'View Details',
    export_data: 'Export Data',

    // Page Builder
    pages: 'Pages',
    create_page: 'Create Page',
    page_title: 'Page Title',
    page_slug: 'Page Slug',
    publish: 'Publish',
    unpublish: 'Unpublish',

    // Hotel Detail Page
    general_information: 'General Information',
    short_description: 'Short Description',
    full_description: 'Full Description',
    generate_with_ai: 'Generate with AI',
    gallery_media: 'Gallery & Media',
    manage_media: 'Manage All Media',
    location_data: 'Location Data',
    latitude: 'Latitude',
    longitude: 'Longitude',
    sync_from_api: 'Sync from Tourvisio',
    discard: 'Discard',
    save_changes: 'Save Changes',
    published: 'Published',
    draft: 'Draft',
    last_updated: 'Last updated',
    last_updated_by: 'by',
    room_mapping: 'Room Mapping',
    social_links: 'Social Links',
    region: 'Region',
    star_rating_label: 'Star Rating',
    max_chars: 'MAX {0} CHARS',
    click_to_upload: 'Click to upload or drag and drop',
    upload_formats: 'SVG, PNG, JPG or GIF (max. 10MB)',
    map_preview_disabled: 'Map Preview Disabled',
    use_api_data: 'Use API Data',
    use_manual_data: 'Use Manual Data',
    api_data: 'API Data',
    manual_data: 'Manual Data',
    syncing: 'Syncing...',
    sync_complete: 'Sync Complete',
    select_data_source: 'Select Data Source',
    sync_from_tourvisio: 'Sync from Tourvisio',
    current_value: 'Current Value',
    new_api_value: 'New API Value',
    confirm_sync: 'Confirm Sync',
    page_not_found: 'Page Not Found',
    page_not_found_desc: 'The page you are looking for does not exist or has been moved.',
    go_to_homepage: 'Go to Homepage',
    back_to_list: 'Back to List',
    hotel_details: 'Hotel Details',
    rooms: 'Rooms',
    amenities: 'Amenities',
    gallery: 'Gallery',

    // Flight Search
    flight_select_btn: 'Select Flight',
    searching_flights_status: 'Searching flights...',
    search_failed_msg: 'Search failed',
    retry_action: 'Retry',
    no_flights_found_msg: 'No flights found',
    price_per_person: 'per person',
    select_action: 'Select',

    // Admin Panel Sidebar
    destinations: 'Destinations',
    tours_cache: 'Tours Cache',
    reisegiganten_export: 'Reisegiganten',
    media_library: 'Media Library',
    content: 'Content',
    pages_menu: 'Pages',
    data_explorer_menu: 'Data Explorer',
    ai_manager: 'AI Manager',
    users: 'Users',
    settings_menu: 'Settings',
    theme_toggle: 'Theme Toggle',
    reporting: 'Reporting',
    ads_management: 'Ad Management',
    social_media: 'Social Media',
    api_management: 'API Management',
    api_diagnostics: 'API Diagnostics',
    seo_tools: 'SEO Tools',
    support_center: 'Support Center',
    mapping: 'Data Mapping',
    data_mapper: 'Data Mapper',
    public_api: 'Public API',
    api_keys: 'API Keys',
    cache_management: 'Cache',
    site_management: 'Site Settings',
    contents: 'Contents',
    tour_assist: 'Tour Assist',
    ai_planner: 'AI Planner',
    static_hotels: 'Static Hotels',
    scraped_hotels: 'Scraped Hotels',
    hotel_scraper: 'Hotel Scraper',
    enriched_content: 'Enriched Content',
    cached_data: 'Cached Data',
    featured_image: 'Featured Image',
    featured_image_desc: 'This image will be shown at the top of the detail page',
    remove_featured_image: 'Remove Featured Image',
    featured_image_removed: 'Featured image removed',
    featured_image_updated: 'Featured image updated',
    fetch_from_website: 'Fetch from Website',
    scraping: 'Scraping...',
    rescrape: 'Rescrape',
    translate_to_english: 'Translate to English',
    translating: 'Translating...',
    no_enriched_content: 'No enriched content available',
    control_panel: 'Control Panel',
    overview_management: 'Overview & Management',

    // Field Mapper
    field_mapper_title: 'Field Mapper',
    field_mapper_subtitle: 'Drag and drop API fields to display fields',
    create_defaults: 'Create Defaults',
    reset: 'Reset',
    new_field: 'New Field',
    entity_hotels: 'Hotels',
    entity_rooms: 'Rooms',
    entity_flights: 'Flights',
    entity_offers: 'Offers',
    entity_destinations: 'Destinations',
    format_text: 'Text',
    format_number: 'Number',
    format_currency: 'Currency',
    format_date: 'Date',
    format_datetime: 'Date/Time',
    format_image: 'Image',
    format_rating: 'Rating',
    format_badge: 'Badge',
    format_list: 'List',
    format_html: 'HTML',
    group_basic: 'Basic Information',
    group_pricing: 'Pricing',
    group_media: 'Media',
    group_features: 'Features',
    group_schedule: 'Scheduling',
    group_airports: 'Airports',
    group_dates: 'Dates',
    group_passengers: 'Passengers',
    group_details: 'Details',
    mappings_load_failed: 'Failed to load mappings',
    order_saved: 'Order saved',
    order_save_failed: 'Failed to save order',
    saved: 'Saved',
    save_failed: 'Save failed',
    field_added: 'New field added',
    fill_all_fields: 'Please fill all fields',
    add_failed: 'Add failed',
    deleted: 'Deleted',
    delete_failed: 'Delete failed',
    defaults_created: 'Default fields created',
    operation_failed: 'Operation failed',
    reset_complete: 'Reset complete',
    no_mappings: 'No mappings for this entity',
    edit_field: 'Edit Field',
    display_name: 'Display Name',
    source_field: 'Source Field (API)',
    format: 'Format',
    group: 'Group',
    icon: 'Icon',
    field_key: 'Field Key (unique)',
    add: 'Add',
    system: 'system',
    visible: 'Visible',
    in_list: 'In List',
    in_card: 'In Card',
    confirm_delete_field: 'Are you sure you want to delete this field?',
    confirm_reset_mappings: 'Are you sure you want to reset mappings?',

    // Field Labels (for i18n in FieldMapper)
    field_hotel_name: 'Hotel Name',
    field_stars: 'Stars',
    field_location: 'Location',
    field_city: 'City',
    field_country: 'Country',
    field_description: 'Description',
    field_thumbnail: 'Main Image',
    field_facilities: 'Facilities',
    field_room_type: 'Room Type',
    field_capacity: 'Capacity',
    field_size: 'Size (m²)',
    field_room_amenities: 'Room Amenities',
    field_airline: 'Airline',
    field_flight_number: 'Flight No',
    field_departure_time: 'Departure',
    field_arrival_time: 'Arrival',
    field_duration: 'Duration',
    field_departure_airport: 'Departure Airport',
    field_arrival_airport: 'Arrival Airport',
    field_total_price: 'Total Price',
    field_per_person: 'Per Person',
    field_check_in: 'Check-in',
    field_check_out: 'Check-out',
    field_nights: 'Nights',
    field_board_type: 'Board Type',
    field_room: 'Room',
    field_adults: 'Adults',
    field_children: 'Children',
    field_destination: 'Destination',
    field_code: 'Code',

    // AI Designer
    ai_designer_title: 'Lumi Assist Website Designer',
    ai_designer_history: 'History',
    ai_designer_history_title: 'Operation History',
    ai_designer_clear_all: 'Clear All',
    ai_designer_no_operations: 'No operations yet',
    ai_designer_undo: 'Undo',
    ai_designer_undone: 'Undone',
    ai_designer_instruction: 'Tell Lumi Assist what changes you want. You can update homepage hero, flash sale banners, or destination details instantly.',
    ai_designer_placeholder: "E.g: 'Update homepage hero for Egypt Winter Sun campaign' or 'Make the flash sale banner red and write 50% off!'",
    ai_designer_apply: 'Apply Changes',
    ai_designer_processing: 'Processing...',
    ai_api_key_missing: 'AI API Key Missing',
    ai_api_key_instruction: 'AI Designer requires the VITE_GEMINI_API_KEY environment variable to work.',
    ai_api_key_add: 'Add to frontend .env file:',
    ai_undo_success: 'Undone:',
    ai_undo_failed: 'Undo failed',
    ai_history_cleared: 'History cleared',
    ai_settings_updated: 'Widget settings updated',
    ai_destination_updated: 'Destination updated:',

    // Admin Search
    search_placeholder: 'Search...',
    search_history: 'Search History',

    // Additional UI Strings
    uploading: 'Uploading...',
    drop_images_or_click: 'Drop images or click to upload',
    fetch_images: 'Fetch Images',
    fetch_data: 'Fetch Data',
    upload_image: 'Upload Image',
    chart_loading: 'Chart Loading...',
    downloading: 'Downloading...',

    // Footer
    footer_company: 'Company',
    footer_about_us: 'About Us',
    footer_terms: 'Terms & Conditions',
    footer_privacy: 'Privacy & Security',
    footer_cancellation: 'Cancellation Policy',
    footer_contact: 'Contact',
    footer_newsletter: 'Newsletter',
    footer_subscribe: 'Subscribe',
    footer_email_placeholder: 'Your email address',
    footer_customer_service: 'Customer Service',
    footer_travel_line: 'Travel Line',
    footer_company_desc: 'Premium package tours to handpicked destinations. We offer tailor-made travel experiences with personal service and quality guarantee.',

    // SearchWidget Preview
    check_in_label: 'Check-in',
    check_out_label: 'Check-out',
    duration_label: 'Duration',
    travelers_label: 'Travelers',
    rooms_label: 'Rooms',
    ages_18_plus: 'Ages 18+',
    ages_0_17: 'Ages 0-17',
    years_old: 'years',
    select_duration_hint: 'Select duration to see dates',

    // Search Results Page UI
    showing_results: 'Showing',
    of_results: 'of',
    loading_more: 'Loading more hotels...',
    try_adjusting: 'Try adjusting your filters or search criteria.',
    featured_offer: 'Featured Offer',
    ai_insights: 'Live AI Insights',
    ai_highlight: 'AI Highlight',
    ai_analyzing: 'TravelMate AI Analyzing...',

    // Hotel Detail Pricing Summary
    total_price_for_nights: 'TOTAL PRICE FOR {0} NIGHTS',
    nights_count: '{0} Nights',
    guests_label: 'Guests',
    adults_count: '{0} Adults',
    children_count: '{0} Children',
    package_includes_desc: 'Includes flight, hotel, transfer and taxes',
    selected_room_label: 'SELECTED ROOM',
    select_room_hint: 'Please select a room above',
    select_room_btn: 'Select a Room',
    secure_booking_msg: 'Secure booking with SSL encryption',
    follow_on_instagram: 'Follow on Instagram',

    // Booking / Payment
    booking_failed: 'Booking Failed',
    payment_failed_generic: 'Payment failed or was cancelled.',
    payment_verifying: 'Verifying payment...',
    payment_processing_wait: 'Payment is being processed...',
    payment_please_wait: 'Please wait, this may take a moment.',
    payment_timeout: 'Payment verification timed out. Your payment may still be processing. Please check your email for confirmation or contact support.',
    payment_verify_error: 'Could not verify payment status. Please contact support.',
    payment_received_title: 'Payment Received',
    payment_received_message: 'Your payment has been received. Your booking is being processed and you will receive a confirmation email shortly. If you do not receive an email within 1 hour, please contact support.',
    payment_received_email_note: 'Check your email inbox and spam folder for the confirmation.',
  },
  sv: {
    // Header
    holidays: 'Paketresor',
    hotels: 'Hotell',
    flights: 'Flyg',

    // Search Widget
    package_tours: 'Paketresor',
    only_hotel: 'Endast Hotell',
    transfer: 'Transfer',
    departure: 'Avresa',
    destination: 'Resmål',
    check_in_date: 'Incheckning',
    night: 'Nätter',
    nationality: 'Nationalitet',
    currency: 'Valuta',
    room_and_person_number: 'Antal Rum och Personer',
    advance_search: 'Avancerad Sökning',
    choose: 'Välj',
    guests: 'Gäster',
    adults: 'Vuxna',
    children: 'Barn',
    child_ages: 'Barnens Ålder',
    child_age: 'Barn {0} Ålder',
    search: 'Sök',

    // Hero & Landing Page
    hero_badge: 'Utforska Världen',
    hero_title_1: 'Upptäck Din',
    hero_title_2: 'Perfekta Resa',
    hero_subtitle: 'Här kan du söka och boka oförglömliga resor med Lumi Tours – vi skapar våra egna noggrant utvalda upplevelser, dedikerade till att ge dig en fantastisk resa från start till mål. Utforska våra exklusiva paket med lyxhotell, flyg och minnesvärda ögonblick, designade helt av oss.',

    feature_fast_title: 'Snabb & Enkel Bokning',
    feature_fast_desc: 'Boka din drömsemester med bara några klick. Omedelbar bekräftelse och säkra betalningar.',
    feature_curated_title: 'Handplockade Upplevelser',
    feature_curated_desc: 'På Lumi Tours söker vi fram och handplockar de bästa resmålen och hotellen – noggrant utvalda just för dig. Varje resa är curated, personlig och skapad för att ge dig en oförglömlig upplevelse från första stund.',
    feature_support_title: 'Support Dygnet Runt',
    feature_support_desc: 'Våra dedikerade reseexperter hjälper dig före, under och efter din resa.',

    inspiration: 'Inspiration',
    popular_destinations: 'Populära Resmål',
    popular_subtitle: 'Utforska våra mest besökta platser den här säsongen och hitta ditt nästa äventyr.',

    // CTA-sektion
    cta_badge: 'Boka Tidigt-Erbjudande',
    cta_title: 'Drömsemester på Blue Dreams Resort',
    cta_desc: 'Planera din drömresa nu och spara upp till 25% på din vistelse. Njut av kristallklart vatten, lyxiga bekvämligheter och oförglömliga stunder på denna topprankade resort i Bodrum.',
    cta_button: 'Upptäck Erbjudandet',

    // Campaign Banner
    campaign_banner_text: "✨ BLIXTREA! Upp till 30% rabatt på alla resor till Turkiet denna vecka! ✨",

    // Membership / Privé Section
    member_title: 'Lås upp Lumi Privé',
    member_desc: 'Gå med i vår inre krets av resenärer. Medlemmar får hemliga priser, kostnadsfria rumsuppgraderingar och förtur till våra kurerade kollektioner.',
    benefit_deals: 'Hemliga Medlemspriser',
    benefit_support: 'Prioriterad Concierge',
    benefit_curated: 'Handplockade Pärlor',
    join_now: 'Lås upp Förmåner',
    enter_email: 'Ange din e-postadress',

    get_exclusive_deals: 'Få Tillgång till Utvalda Resor',
    newsletter_desc: 'Prenumerera på vårt nyhetsbrev och få exklusiva, handplockade reseupplevelser som endast delas med våra medlemmar.',
    subscribe: 'Prenumerera',

    // Holidays Page
    holidays_hero_title: 'Oförglömliga Resor',
    holidays_hero_subtitle: 'Från soliga stränder till kulturella stadsresor, hitta det kompletta semesterpaketet som passar din stil.',
    our_services: 'Våra Premiumtjänster',
    service_flights_title: 'Globala Flyg',
    service_flights_desc: 'Tillgång till stora flygbolag och exklusiva charterflyg till toppdestinationer världen över.',
    service_hotels_title: 'Handplockade Hotell',
    service_hotels_desc: 'Från boutiquehotell till 5-stjärniga all-inclusive-resorter, vi väljer det bästa för dig.',
    service_transfers_title: 'Smidiga Transfers',
    service_transfers_desc: 'Privata och delade flygplatstransfers som säkerställer en smidig start på din semester.',
    trending_packages: 'Trendiga Paket',
    view_package: 'Visa Paket',
    why_choose_us: 'Varför Välja LumiTours?',
    why_desc_1: 'Expertutformade resplaner för den moderna resenären.',
    why_desc_2: 'Fullständigt ekonomiskt skydd och säker bokningsteknik.',
    why_desc_3: 'Lokala partnerskap som säkerställer autentiska upplevelser.',

    // Landing Page Sub-pages
    landing_holidays_title: 'Oförglömliga Semestrar',
    landing_holidays_subtitle: 'Sol, hav och sand. Upptäck världens bästa paketresor.',
    landing_holidays_badge: 'All Inclusive-erbjudanden',
    landing_hotels_title: 'Hitta Ditt Perfekta Boende',
    landing_hotels_subtitle: 'Från mysiga boutiquehotell till 5-stjärniga lyxresorter.',
    landing_hotels_badge: 'Premiumboenden',
    landing_flights_title: 'Charterflyg & Paket',
    landing_flights_subtitle: 'Direktflyg till populära semesterdestinationer.',
    landing_flights_badge: 'Lyft',
    landing_last_minute_title: 'Sista Minuten-erbjudanden',
    landing_last_minute_desc: 'Fynda bland avgångar inom de närmaste 4 veckorna.',
    landing_best_price_title: 'Bästa Prisgarantin',
    landing_best_price_desc: 'Vi jämför priser från hundratals leverantörer.',
    landing_secure_booking_title: 'Säker Bokning',
    landing_secure_booking_desc: 'Dina betalningar och uppgifter är alltid skyddade.',

    // Flights Page
    flights_hero_title: 'Hitta Dina Vingar',
    flights_hero_subtitle: 'Sök och jämför billiga flyg från alla större flygbolag. Din nästa resa börjar här.',
    top_flight_destinations: 'Topp Flygdestinationer',
    top_flight_subtitle: 'Upptäck våra mest populära rutter och boka din drömbiljett idag.',
    one_way: 'Enkel Resa',
    round_trip: 'Tur & Retur',
    departure_airport: 'Avreseflygplats',
    arrival_airport: 'Ankomstflygplats',
    departure_date: 'Avresedatum',
    return_date: 'Returdatum',
    passengers: 'Passagerare',
    infants: 'Spädbarn',

    // Search Results
    search_again: 'Sök Igen',
    search_filters: 'SÖKFILTER',
    reset_all: 'Återställ Alla',
    hotel_search: 'Hotellsökning',
    hotel_price: 'Hotellpris',
    hotel_stars: 'Hotellstjärnor',
    hotel_boards: 'Hotellpension',
    hotel_locations: 'Hotellplatser',
    hotel_themes: 'Hotellteman',
    hotel_facilities: 'Hotellfaciliteter',
    options: 'ALTERNATIV',
    matches_your_search_criteria: 'Matchar dina sökkriterier',
    sort_by: 'Sortera efter',
    compare: 'Jämför',
    show_on_map: 'Visa på Karta',
    social_media_share: 'Dela på Sociala Medier',
    hotel_description: 'Hotellbeskrivning',
    included: 'Inkluderat',
    room_price_starting_from: 'Rumspris Från',
    filters: 'Filter',
    price_range: 'Prisintervall',
    star_rating: 'Stjärnbetyg',
    all: 'Alla',
    view_deal: 'Visa Erbjudande',
    book_now: 'Boka Nu',
    per_night: 'per natt',
    total_price: 'Totalt Pris',
    per_person: 'per person',
    no_results: 'Inga resultat hittades. Försök med en annan sökning.',
    back_to_search: 'Tillbaka till Sök',
    searching_for_deals: 'Söker efter de bästa erbjudandena...',

    // Booking & Payment
    passenger_details: 'Passageraruppgifter',
    contact_details: 'Kontaktuppgifter',
    payment_details: 'Betalningsuppgifter',
    first_name: 'Förnamn',
    last_name: 'Efternamn',
    birth_date: 'Födelsedatum',
    gender: 'Kön',
    male: 'Man',
    female: 'Kvinna',
    email: 'E-post',
    phone: 'Telefon',
    address: 'Adress',
    city: 'Stad',
    zip_code: 'Postnummer',
    country: 'Land',
    card_number: 'Kortnummer',
    card_holder: 'Kortinnehavarens Namn',
    expiry_date: 'Giltighetsdatum',
    cvv: 'CVV/CVC',
    pay_now: 'Betala & Bekräfta',
    booking_summary: 'Bokningsöversikt',
    check_in: 'Incheckning',
    check_out: 'Utcheckning',
    nights: 'Nätter',
    room_type: 'Rumstyp',
    board_basis: 'Pension',
    total_amount: 'Totalt Belopp',
    secure_payment: 'Säker Betalning',
    reservation_success: 'Bokning Lyckades!',
    reservation_number: 'Bokningsnummer',
    thank_you_message: 'Tack för din bokning. En bekräftelse har skickats till din e-post.',
    back_to_home: 'Tillbaka till Startsidan',
    processing: 'Bearbetar...',
    adult: 'Vuxen',
    child: 'Barn',
    passenger: 'Passagerare',
    booking_overview: 'Bokningsöversikt',
    complete_booking_msg: 'Fyll i dina uppgifter för att slutföra bokningen',
    back_to_hotel: 'Tillbaka till Hotellet',
    lead_passenger: 'Huvudpassagerare',
    proceed_to_payment: 'Fortsätt till Betalning →',
    price_change: 'Prisändring',
    price_breakdown: 'Prisuppdelning',
    all_taxes_included: 'Alla skatter inkluderade',
    your_trip: 'Din Resa',
    booking_valid_for: 'Bokningen är giltig i',
    total_amount_label: 'totalbelopp',
    adults_short: 'vuxna',
    children_short: 'barn',
    room: 'rum',
    flight: 'Flyg',
    share_trip: 'Dela resan',
    link_copied: 'Länk kopierad!',
    flight_change: 'flygbyte',
    show_full_flight_info: 'Visa fullständig flyginformation',
    start_new_session: 'Starta ny session',
    baggage_selection: 'Bagageval',
    extra_baggage: 'Extra Bagage',

    // Payment Page
    payment_initializing: 'Initierar betalningssystem...',
    payment_connecting: 'Ansluter till bokningssystem...',
    payment_fetching_methods: 'Hämtar betalningsmetoder...',
    payment_confirming: 'Bekräftar bokning...',
    payment_processing: 'Bearbetar betalning...',
    payment_redirecting: 'Omdirigerar till betalningssida...',
    payment_redirecting_short: 'Omdirigerar...',
    payment_connection_issue: 'Anslutningsproblem',
    payment_retry: 'Försök igen',
    payment_select_method: 'Välj betalningsmetod',
    payment_no_methods: 'Inga betalningsmetoder hittades. Försök igen senare.',
    payment_redirect_info: 'Du kommer att omdirigeras till vår säkra betalningspartner för att slutföra transaktionen.',
    payment_accept_terms_prefix: 'Jag godkänner',
    payment_terms_title: 'Villkor och Bestämmelser',
    payment_accept_close: 'Godkänn & Stäng',
    payment_proceed: 'Fortsätt till Säker Betalning',

    // TravelMate
    view_hotel: 'Visa Hotell',
    travelmate_ai: 'Lumi Assist',
    beta: 'Beta',
    your_budget: 'Din Budget',
    gap: 'Gap',
    market_avg: 'Marknadsmedel',
    est_total: 'Beräknat Totalt',

    // Chat / Live Support
    chat_support: "Live-support",
    chat_ai_assistant: "AI-assistent",
    chat_agent_active: "Agent Aktiv",
    chat_waiting_agent: "Väntar på agent...",
    chat_request_agent: "Behöver du mänsklig hjälp? Begär Agent",
    chat_type_message: "Skriv ett meddelande...",
    chat_listening: "Lyssnar...",
    chat_welcome: "Hej! Jag heter Luna, din virtuella reseassistent. Jag kan kolla priser, hjälpa dig att boka eller svara på frågor. Vad kan jag hjälpa dig med idag?",
    chat_agent_connected: "Kopplar dig till en mänsklig agent...",
    chat_agent_notified: "Jag har meddelat en mänsklig agent. De kommer att vara med dig strax. Beräknad väntetid: 2 minuter.",

    // Footer
    copyright: '© 2025 Lumitours. Alla rättigheter förbehållna.',
    admin_login: 'Admin',

    // Admin
    dashboard: 'Översikt',
    bookings: 'Bokningar',
    my_bookings: 'Mina Bokningar',
    manage_reservations: 'Hantera dina reservationer och dokument',
    content_manager: 'Innehållshanterare',
    logs: 'Systemloggar',
    settings: 'API-inställningar',
    logout: 'Logga ut',
    admin_panel: 'Adminpanel',
    recent_bookings: 'Senaste Bokningar',
    total_revenue: 'Totala Intäkter',
    active_users: 'Aktiva Användare',
    system_status: 'Systemstatus',
    inspect_product: 'Inspektera Produkt',
    enter_product_id: 'Ange Produkt-ID (t.ex. 8007)',
    fetch_details: 'Hämta Detaljer',
    raw_data: 'Rådata',
    ai_designer: 'AI-Designer',
    live_support: 'Live-support',
    exit_to_app: 'Tillbaka till appen',

    // Settings
    api_settings: 'API-inställningar',
    service_address: 'Tjänsteadress',
    agency: 'Byrå',
    api_user: 'Användare',
    api_password: 'Lösenord',
    save_settings: 'Spara Inställningar',
    cancel: 'Avbryt',
    settings_saved: 'Inställningar sparade!',
    api_settings_not_configured: 'API-inställningarna är inte konfigurerade. Vänligen konfigurera dem först.',
    api_search_error: 'Ett fel uppstod vid sökning. Kontrollera konsolen för detaljer.',
    test_connection: 'Testa Anslutning',
    api_test_tool: 'API-testverktyg',
    test_instructions: 'Klicka på knappen för att skicka en testförfrågan till den konfigurerade tjänsteadressen med de sparade uppgifterna.',
    testing: 'Testar...',
    test_success: 'Lyckades! API:et svarade.',
    test_failed: 'Misslyckades. Se svar/fel nedan.',
    cors_error_help: 'Ett nätverksfel uppstod. Detta är ofta ett CORS-problem. Se till att servern på tjänsteadressen tillåter förfrågningar från denna ursprung.',
    test_response: 'API-svar',

    // API Logs
    api_logs: 'API-loggar',
    request: 'Förfrågan',
    response: 'Svar',

    // Flight Options Modal
    flight_options_title: 'Flygalternativ',
    flight_options_subtitle: 'Visa och välj alternativa flyg',
    outbound_flight: 'Utresa',
    inbound_flight: 'Hemresa',
    all_airlines: 'Alla Flygbolag',
    direct_flight: 'Direktflyg',
    max_1_stop: 'Max 1 Byte',
    max_2_stops: 'Max 2 Byten',
    loading_flights: 'Laddar flyg...',
    no_flights_found_criteria: 'Inga flyg hittades som matchar dessa kriterier.',
    total_difference: 'Total Skillnad',
    confirm_flights: 'Bekräfta Flyg',
    selected: 'Vald',
    stops: 'Byten',
    any: 'Alla',
    clear_filters: 'Rensa filter',
    tour_details: 'Resedetaljer',
    alternative_flights: 'Alternativa flyg',
    select_room: 'Välj rum',
    total_price_label: 'Totalt pris',
    view_flight_options: 'Visa flygalternativ',
    no_flights_found: 'Inga flyg hittades',
    close: 'Stäng',
    confirm: 'Bekräfta',
    error_loading_flights: 'Ett fel uppstod när flyginformationen skulle hämtas',

    // Admin Sub-components
    hotel_manager: 'Hotellhanterare',
    tour_cache: 'Turcache',
    data_explorer: 'Datautforskare',
    page_builder: 'Sidbyggare',
    field_mapper: 'Fältmappare',
    cms_manager: 'CMS-hanterare',

    // Common Admin Actions
    add_new: 'Lägg till ny',
    edit: 'Redigera',
    delete: 'Radera',
    save: 'Spara',
    refresh: 'Uppdatera',
    loading: 'Laddar...',
    no_data: 'Ingen data tillgänglig',
    actions: 'Åtgärder',
    status: 'Status',
    name: 'Namn',
    description: 'Beskrivning',
    created: 'Skapad',
    updated: 'Uppdaterad',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    confirm_delete: 'Är du säker på att du vill radera detta?',

    // Hotel Manager
    hotels_list: 'Hotelllista',
    add_hotel: 'Lägg till hotell',
    edit_hotel: 'Redigera hotell',
    hotel_name: 'Hotellnamn',
    hotel_location: 'Plats',
    hotel_rating: 'Betyg',
    hotel_featured: 'Utvalt',
    sync_hotels: 'Synkronisera hotell',

    // Tour Cache
    tour_cache_manager: 'Turcache Hanterare',
    queries: 'Sökningar',
    data: 'Data',
    statistics: 'Statistik',
    backups: 'Säkerhetskopior',
    new_query: 'Ny Sökning',
    query_name: 'Sökningens Namn',
    departure_point: 'Avreseort',
    arrival_point: 'Ankomstort',
    check_in_start: 'Incheckning Start',
    check_in_end: 'Incheckning Slut',
    cache_days: 'Cachedagar',
    schedule: 'Schema',
    automatic_schedule: 'Automatiskt Schema',
    work_hour: 'Arbetstid',
    save_query: 'Spara Sökning',
    saving: 'Sparar...',
    cancel_edit: 'Avbryt',
    no_queries: 'Inga sökningar hittades.',
    run: 'Kör',
    delete_query_confirm: 'Är du säker på att du vill ta bort denna sökning?',
    total_queries: 'Totalt Antal Sökningar',
    scheduled: 'Schemalagd',
    stop: 'Stopp',
    cache_queries: 'Cacheförfrågningar',
    add_query: 'Lägg till förfrågan',
    run_query: 'Kör förfrågan',
    cache_status: 'Cachestatus',
    last_run: 'Senast körd',
    total_results: 'Totala resultat',

    // Data Explorer
    select_entity: 'Välj entitet',
    view_details: 'Visa detaljer',
    export_data: 'Exportera data',

    // Page Builder
    pages: 'Sidor',
    create_page: 'Skapa sida',
    page_title: 'Sidtitel',
    page_slug: 'Sidslug',
    publish: 'Publicera',
    unpublish: 'Avpublicera',

    // Hotel Detail Page
    general_information: 'Allmän Information',
    short_description: 'Kort Beskrivning',
    full_description: 'Fullständig Beskrivning',
    generate_with_ai: 'Generera med AI',
    gallery_media: 'Galleri & Media',
    manage_media: 'Hantera All Media',
    location_data: 'Platsdata',
    latitude: 'Latitud',
    longitude: 'Longitud',
    sync_from_api: 'Synka från Tourvisio',
    discard: 'Ångra',
    save_changes: 'Spara Ändringar',
    published: 'Publicerad',
    draft: 'Utkast',
    last_updated: 'Senast uppdaterad',
    last_updated_by: 'av',
    room_mapping: 'Rumsmappning',
    social_links: 'Sociala Länkar',
    region: 'Region',
    star_rating_label: 'Stjärnbetyg',
    max_chars: 'MAX {0} TECKEN',
    click_to_upload: 'Klicka för att ladda upp eller dra och släpp',
    upload_formats: 'SVG, PNG, JPG eller GIF (max. 10MB)',
    map_preview_disabled: 'Kartförhandsvisning Inaktiverad',
    use_api_data: 'Använd API-data',
    use_manual_data: 'Använd Manuell Data',
    api_data: 'API Data',
    manual_data: 'Manuell Data',
    syncing: 'Synkar...',
    sync_complete: 'Synkning Klar',
    select_data_source: 'Välj Datakälla',
    sync_from_tourvisio: 'Synka från Tourvisio',
    current_value: 'Nuvarande Värde',
    new_api_value: 'Nytt API-värde',
    confirm_sync: 'Bekräfta Synkning',
    page_not_found: 'Sidan Hittades Inte',
    page_not_found_desc: 'Sidan du letar efter finns inte eller har flyttats.',
    go_to_homepage: 'Gå till Startsidan',
    back_to_list: 'Tillbaka till Lista',
    hotel_details: 'Hotelldetaljer',
    rooms: 'Rum',
    amenities: 'Bekvämligheter',
    gallery: 'Galleri',

    // Flight Search
    flight_select_btn: 'Välj Flyg',
    searching_flights_status: 'Söker flyg...',
    search_failed_msg: 'Sökning misslyckades',
    retry_action: 'Försök igen',
    no_flights_found_msg: 'Inga flyg hittades',
    price_per_person: 'per person',
    select_action: 'Välj',

    // Admin Panel Sidebar
    destinations: 'Destinationer',
    tours_cache: 'Turcache',
    reisegiganten_export: 'Reisegiganten',
    media_library: 'Mediabibliotek',
    content: 'Innehåll',
    pages_menu: 'Sidor',
    data_explorer_menu: 'Datautforskare',
    ai_manager: 'AI-Hanterare',
    users: 'Användare',
    settings_menu: 'Inställningar',
    theme_toggle: 'Temaväxling',
    reporting: 'Rapportering',
    ads_management: 'Annonser',
    social_media: 'Sociala Medier',
    api_management: 'API-hantering',
    api_diagnostics: 'API Diagnostik',
    seo_tools: 'SEO-verktyg',
    support_center: 'Supportcenter',
    mapping: 'Datamappning',
    data_mapper: 'Datamappare',
    public_api: 'Publikt API',
    api_keys: 'API-nycklar',
    cache_management: 'Cache',
    site_management: 'Webbplatsinställningar',
    contents: 'Innehåll',
    tour_assist: 'Tour Assist',
    ai_planner: 'AI Reseplanerare',
    static_hotels: 'Statiska Hotell',
    scraped_hotels: 'Skrapade Hotell',
    hotel_scraper: 'Hotellskrapare',
    enriched_content: 'Berikad Innehåll',
    cached_data: 'Cachad Data',
    featured_image: 'Utvald Bild',
    featured_image_desc: 'Denna bild visas överst på detaljsidan',
    remove_featured_image: 'Ta bort utvald bild',
    featured_image_removed: 'Utvald bild borttagen',
    featured_image_updated: 'Utvald bild uppdaterad',
    fetch_from_website: 'Hämta från Webbplats',
    scraping: 'Skrapar...',
    rescrape: 'Skrapa Igen',
    translate_to_english: 'Översätt till Engelska',
    translating: 'Översätter...',
    no_enriched_content: 'Ingen berikad innehåll tillgänglig',
    control_panel: 'Kontrollpanel',
    overview_management: 'Översikt & Hantering',

    // Field Mapper
    field_mapper_title: 'Fältmappare',
    field_mapper_subtitle: 'Dra och släpp API-fält till visningsfält',
    create_defaults: 'Skapa Standardvärden',
    reset: 'Återställ',
    new_field: 'Nytt Fält',
    entity_hotels: 'Hotell',
    entity_rooms: 'Rum',
    entity_flights: 'Flyg',
    entity_offers: 'Erbjudanden',
    entity_destinations: 'Destinationer',
    format_text: 'Text',
    format_number: 'Nummer',
    format_currency: 'Valuta',
    format_date: 'Datum',
    format_datetime: 'Datum/Tid',
    format_image: 'Bild',
    format_rating: 'Betyg',
    format_badge: 'Märke',
    format_list: 'Lista',
    format_html: 'HTML',
    group_basic: 'Grundläggande Information',
    group_pricing: 'Prissättning',
    group_media: 'Media',
    group_features: 'Funktioner',
    group_schedule: 'Schemaläggning',
    group_airports: 'Flygplatser',
    group_dates: 'Datum',
    group_passengers: 'Passagerare',
    group_details: 'Detaljer',
    mappings_load_failed: 'Kunde inte ladda mappningar',
    order_saved: 'Ordning sparad',
    order_save_failed: 'Kunde inte spara ordning',
    saved: 'Sparad',
    save_failed: 'Kunde inte spara',
    field_added: 'Nytt fält tillagt',
    fill_all_fields: 'Fyll i alla fält',
    add_failed: 'Kunde inte lägga till',
    deleted: 'Raderad',
    delete_failed: 'Kunde inte radera',
    defaults_created: 'Standardfält skapade',
    operation_failed: 'Åtgärden misslyckades',
    reset_complete: 'Återställning klar',
    no_mappings: 'Inga mappningar för denna entitet',
    edit_field: 'Redigera Fält',
    display_name: 'Visningsnamn',
    source_field: 'Källfält (API)',
    format: 'Format',
    group: 'Grupp',
    icon: 'Ikon',
    field_key: 'Fältnyckel (unik)',
    add: 'Lägg till',
    system: 'system',
    visible: 'Synlig',
    in_list: 'I Lista',
    in_card: 'I Kort',
    confirm_delete_field: 'Är du säker på att du vill radera detta fält?',
    confirm_reset_mappings: 'Är du säker på att du vill återställa mappningarna?',

    // Field Labels (for i18n in FieldMapper)
    field_hotel_name: 'Hotellnamn',
    field_stars: 'Stjärnor',
    field_location: 'Plats',
    field_city: 'Stad',
    field_country: 'Land',
    field_description: 'Beskrivning',
    field_thumbnail: 'Huvudbild',
    field_facilities: 'Faciliteter',
    field_room_type: 'Rumstyp',
    field_capacity: 'Kapacitet',
    field_size: 'Storlek (m²)',
    field_room_amenities: 'Rumsfaciliteter',
    field_airline: 'Flygbolag',
    field_flight_number: 'Flygnummer',
    field_departure_time: 'Avgång',
    field_arrival_time: 'Ankomst',
    field_duration: 'Längd',
    field_departure_airport: 'Avgångsflygplats',
    field_arrival_airport: 'Ankomstflygplats',
    field_total_price: 'Totalpris',
    field_per_person: 'Per person',
    field_check_in: 'Incheckning',
    field_check_out: 'Utcheckning',
    field_nights: 'Nätter',
    field_board_type: 'Helpension',
    field_room: 'Rum',
    field_adults: 'Vuxna',
    field_children: 'Barn',
    field_destination: 'Destination',
    field_code: 'Kod',

    // AI Designer
    ai_designer_title: 'Lumi Assist Webbdesigner',
    ai_designer_history: 'Historik',
    ai_designer_history_title: 'Operationshistorik',
    ai_designer_clear_all: 'Rensa allt',
    ai_designer_no_operations: 'Inga operationer ännu',
    ai_designer_undo: 'Ångra',
    ai_designer_undone: 'Ångrad',
    ai_designer_instruction: 'Berätta för Lumi Assist vilka ändringar du vill ha. Du kan uppdatera startsidans hero, kampanjbanners eller destinationsdetaljer direkt.',
    ai_designer_placeholder: "T.ex: 'Uppdatera startsidans hero för Egypten Vintersol-kampanj' eller 'Gör kampanjbannern röd och skriv 50% rabatt!'",
    ai_designer_apply: 'Tillämpa ändringar',
    ai_designer_processing: 'Bearbetar...',
    ai_api_key_missing: 'AI API-nyckel saknas',
    ai_api_key_instruction: 'AI Designer kräver miljövariabeln VITE_GEMINI_API_KEY för att fungera.',
    ai_api_key_add: 'Lägg till i frontend .env-fil:',
    ai_undo_success: 'Ångrad:',
    ai_undo_failed: 'Ångra misslyckades',
    ai_history_cleared: 'Historik rensad',
    ai_settings_updated: 'Widget-inställningar uppdaterade',
    ai_destination_updated: 'Destination uppdaterad:',

    // Admin Search
    search_placeholder: 'Sök...',
    search_history: 'Sökhistorik',

    // Additional UI Strings
    uploading: 'Laddar upp...',
    drop_images_or_click: 'Släpp bilder eller klicka för att ladda upp',
    fetch_images: 'Hämta bilder',
    fetch_data: 'Hämta data',
    upload_image: 'Ladda upp bild',
    chart_loading: 'Diagram laddas...',
    downloading: 'Laddar ner...',

    // Footer
    footer_company: 'Företaget',
    footer_about_us: 'Om oss',
    footer_terms: 'Resevillkor',
    footer_privacy: 'Integritet & Säkerhet',
    footer_cancellation: 'Avbokningsvillkor',
    footer_contact: 'Kontakt',
    footer_newsletter: 'Nyhetsbrev',
    footer_subscribe: 'Prenumerera',
    footer_email_placeholder: 'Din e-postadress',
    footer_customer_service: 'Kundtjänst',
    footer_travel_line: 'Ring Oss',
    footer_company_desc: 'Premium paketresor till handplockade destinationer. Vi erbjuder skräddarsydda reseupplevelser med personlig service och kvalitetsgaranti.',

    // SearchWidget Preview
    check_in_label: 'Incheckning',
    check_out_label: 'Utcheckning',
    duration_label: 'Varaktighet',
    travelers_label: 'Resenärer',
    rooms_label: 'Rum',
    ages_18_plus: 'Ålder 18+',
    ages_0_17: 'Ålder 0-17',
    years_old: 'år',
    select_duration_hint: 'Välj antal nätter för att se datum',

    // Search Results Page UI
    showing_results: 'Visar',
    of_results: 'av',
    loading_more: 'Laddar fler hotell...',
    try_adjusting: 'Prova att justera dina filter eller sökkriterier.',
    featured_offer: 'Utvalt Erbjudande',
    ai_insights: 'AI-insikter i Realtid',
    ai_highlight: 'AI-rekommendation',
    ai_analyzing: 'TravelMate AI Analyserar...',

    // Hotel & Flight Details (New)
    loading_room_details: 'Laddar rumsuppgifter...',
    room_size: 'Storlek',
    bed_type: 'Sängar',
    booking_verified: 'Verifierad av Booking.com',
    no_room_details: 'Inga specifika visuella detaljer hittades för denna rumstyp.',
    total_price_for_nights: 'TOTALPRIS FÖR {0} NÄTTER',
    package_includes_desc: 'Inkluderar flyg, hotell, transfer och skatter',
    selected_room_label: 'VALT RUM',
    select_room_hint: 'Vänligen välj ett rum ovan',
    select_room_btn: 'Välj ett rum',
    secure_booking_msg: 'Säker bokning • Omedelbar bekräftelse',
    follow_on_instagram: 'Följ på Instagram',
    map_unavailable: 'Kartförhandsvisning ej tillgänglig',
    alternative_flights_title: 'Alternativa flygningar',
    current_selection_label: 'Nuvarande val',
    no_alternative_flights_available: 'Inga alternativa flygningar tillgängliga för detta rum.',
    try_another_room: 'Prova ett annat rumsalternativ.',
    alternatives_found_label: '{0} alternativ hittade',
    loading_flight_details: 'Laddar flyginformation...',
    click_to_select: 'Klicka för att välja →',
    select_this_flight: 'Välj detta flyg',
    searching_alternative_flights: 'Söker alternativa flygningar...',
    return_flight: 'Hemresa',
    flight_details_title: 'Flygdetaljer',
    complete_journey_info: 'Komplett reseinformation',
    outbound_flight_label: 'Utresa',
    return_flight_label: 'Hemresa',
    journey_details_label: 'Resedetaljer',
    layover_label: 'Byte',
    package_includes_title: 'Paketet inkluderar',
    flights_label: 'Flyg',
    hotel_label: 'Hotell',
    transfer_label: 'Transfer',
    taxes_label: 'Skatter',
    no_baggage_warning: 'Denna flygning inkluderar inte bagage. Du kan lägga till bagage genom att välja ett paket nedan.',
    no_baggage_info: 'Det finns inget bagage på denna flygning. Du kan lägga till bagage genom att välja en högre biljettklass i nästa steg.',

    // Description Templates
    the_resort_area: 'semesterorten',
    excellent_service: 'utmärkt service',
    hotel_description_template: 'Njut av en fantastisk vistelse på ${hotelName} i ${city}. Detta ${stars}-stjärniga hotell erbjuder ${board} och är det perfekta valet för din semester.',

    // Additional UI
    follow: 'Följ',
    the_resort_area_text: 'semesterorten',
    stars_label: 'Stjärnor',
    nights_count: '{0} Nätter',
    guests_label: 'Resenärer',
    adults_count: '{0} Vuxna',
    children_count: '{0} Barn',

    // Booking / Payment
    booking_failed: 'Bokningen Misslyckades',
    payment_failed_generic: 'Betalningen misslyckades eller avbröts.',
    payment_verifying: 'Verifierar betalning...',
    payment_processing_wait: 'Betalningen behandlas...',
    payment_please_wait: 'Vänligen vänta, det kan ta en stund.',
    payment_timeout: 'Betalningsverifieringen gick ut. Din betalning kan fortfarande behandlas. Kontrollera din e-post för bekräftelse eller kontakta kundtjänst.',
    payment_verify_error: 'Kunde inte verifiera betalningsstatus. Kontakta kundtjänst.',
    payment_received_title: 'Betalning Mottagen',
    payment_received_message: 'Din betalning har mottagits. Din bokning bearbetas och du kommer att få ett bekräftelsemail inom kort. Om du inte får ett mail inom 1 timme, vänligen kontakta kundtjänst.',
    payment_received_email_note: 'Kontrollera din inkorg och skräppost för bekräftelsen.',
  },
  da: {
    // Header
    holidays: 'Ferier',
    hotels: 'Hoteller',
    flights: 'Fly',

    // Search Widget
    package_tours: 'Pakkerejser',
    only_hotel: 'Kun Hotel',
    transfer: 'Transfer',
    departure: 'Afrejse',
    destination: 'Destination',
    check_in_date: 'Check-in',
    night: 'Nætter',
    nationality: 'Nationalitet',
    currency: 'Valuta',
    room_and_person_number: 'Antal Rum og Gæster',
    advance_search: 'Avanceret Søgning',
    choose: 'Vælg',
    guests: 'Gæster',
    adults: 'Voksne',
    children: 'Børn',
    child_ages: 'Børns Alder',
    child_age: 'Barn {0} Alder',
    search: 'Søg',

    // Hero & Landing Page
    hero_badge: 'Udforsk Verden',
    hero_title_1: 'Opdag Din',
    hero_title_2: 'Perfekte Rejse',
    hero_subtitle: 'Her kan du søge og booke uforglemmelige rejser med Lumi Tours – vi skaber vores egne omhyggeligt udvalgte oplevelser, dedikeret til at give dig en fantastisk rejse fra start til slut. Udforsk vores eksklusive pakker med luksushoteller, fly og mindeværdige øjeblikke, designet helt af os.',

    feature_fast_title: 'Hurtig & Nem Booking',
    feature_fast_desc: 'Book din drømmeferie med blot et par klik. Øjeblikkelig bekræftelse og sikre betalinger.',
    feature_curated_title: 'Håndplukkede Oplevelser',
    feature_curated_desc: 'Hos Lumi Tours finder og håndplukker vi de bedste destinationer og hoteller – omhyggeligt udvalgt til dig. Hver rejse er kurateret, personlig og skabt for at give dig en uforglemmelig oplevelse fra første øjeblik.',
    feature_support_title: 'Support Døgnet Rundt',
    feature_support_desc: 'Vores dedikerede rejseeksperter hjælper dig før, under og efter din rejse.',

    inspiration: 'Inspiration',
    popular_destinations: 'Populære Destinationer',
    popular_subtitle: 'Udforsk vores mest besøgte steder denne sæson og find dit næste eventyr.',

    // CTA Section
    cta_badge: 'Tidlig Booking Tilbud',
    cta_title: 'Drømmeferie på Blue Dreams Resort',
    cta_desc: 'Planlæg din drømmerejse nu og spar op til 25% på dit ophold. Nyd krystalklart vand, luksusfaciliteter og uforglemmelige øjeblikke på dette topbedømte resort i Bodrum.',
    cta_button: 'Opdag Tilbuddet',

    // Campaign Banner
    campaign_banner_text: '✨ LYNUDSALG! Op til 30% rabat på alle rejser til Tyrkiet denne uge! ✨',

    // Membership / Privé Section
    member_title: 'Lås Op for Lumi Privé',
    member_desc: 'Bliv en del af vores inderkreds af rejsende. Medlemmer får hemmelige priser, gratis værelsesopgraderinger og prioriteret adgang til vores kuraterede kollektioner.',
    benefit_deals: 'Hemmelige Medlemspriser',
    benefit_support: 'Prioriteret Concierge',
    benefit_curated: 'Håndplukkede Perler',
    join_now: 'Lås Op for Fordele',
    enter_email: 'Indtast din e-mail adresse',

    get_exclusive_deals: 'Få Adgang til Udvalgte Rejser',
    newsletter_desc: 'Tilmeld dig vores nyhedsbrev og modtag eksklusive, håndplukkede rejseoplevelser, som kun deles med vores medlemmer.',
    subscribe: 'Tilmeld',

    // Holidays Page
    holidays_hero_title: 'Uforglemmelige Rejser',
    holidays_hero_subtitle: 'Fra solrige strande til kulturelle byferie, find den komplette feriepakke der passer til din stil.',
    our_services: 'Vores Premium Tjenester',
    service_flights_title: 'Globale Fly',
    service_flights_desc: 'Adgang til store flyselskaber og eksklusive charterfly til topdestinationer verden over.',
    service_hotels_title: 'Håndplukkede Hoteller',
    service_hotels_desc: 'Fra boutique-hoteller til 5-stjernede all-inclusive resorts, vi vælger det bedste for dig.',
    service_transfers_title: 'Smidige Transfers',
    service_transfers_desc: 'Private og delte lufthavnstransfers, der sikrer en problemfri start på din ferie.',
    trending_packages: 'Trending Pakker',
    view_package: 'Se Pakke',
    why_choose_us: 'Hvorfor Vælge LumiTours?',
    why_desc_1: 'Ekspertudformede rejseplaner for den moderne rejsende.',
    why_desc_2: 'Fuld økonomisk beskyttelse og sikker booking-teknologi.',
    why_desc_3: 'Lokale partnerskaber der sikrer autentiske oplevelser.',

    // Landing Page Sub-pages
    landing_holidays_title: 'Uforglemmelige Ferier',
    landing_holidays_subtitle: 'Sol, hav og strand. Opdag verdens bedste pakkerejser.',
    landing_holidays_badge: 'All Inclusive Tilbud',
    landing_hotels_title: 'Find Dit Perfekte Ophold',
    landing_hotels_subtitle: 'Fra hyggelige boutique-hoteller til 5-stjernede luksusresorts.',
    landing_hotels_badge: 'Premium Ophold',
    landing_flights_title: 'Charterfly & Pakker',
    landing_flights_subtitle: 'Direkte fly til populære feriedestinationer.',
    landing_flights_badge: 'Flyv Højt',
    landing_last_minute_title: 'Last Minute Tilbud',
    landing_last_minute_desc: 'Snup et kup på afrejser inden for de næste 4 uger.',
    landing_best_price_title: 'Bedste Pris Garanti',
    landing_best_price_desc: 'Vi sammenligner priser fra hundredvis af udbydere.',
    landing_secure_booking_title: 'Sikker Booking',
    landing_secure_booking_desc: 'Dine betalinger og data er altid beskyttet.',

    // Flights Page
    flights_hero_title: 'Find Dine Vinger',
    flights_hero_subtitle: 'Søg og sammenlign billige fly fra alle store flyselskaber. Din næste rejse starter her.',
    top_flight_destinations: 'Top Fly Destinationer',
    top_flight_subtitle: 'Opdag vores mest populære ruter og book din drømmebillet i dag.',
    one_way: 'Enkelt',
    round_trip: 'Tur/retur',
    departure_airport: 'Afrejselufthavn',
    arrival_airport: 'Ankomstlufthavn',
    departure_date: 'Afrejsedato',
    return_date: 'Returdato',
    passengers: 'Passagerer',
    infants: 'Spædbørn',

    // Search Results
    search_again: 'Søg Igen',
    search_filters: 'SØGEFILTRE',
    reset_all: 'Nulstil Alle',
    hotel_search: 'Hotelsøgning',
    hotel_price: 'Hotelpris',
    hotel_stars: 'Hotelstjerner',
    hotel_boards: 'Forplejning',
    hotel_locations: 'Hotelbeliggenheder',
    hotel_themes: 'Hoteltemaer',
    hotel_facilities: 'Hotelfaciliteter',
    options: 'MULIGHEDER',
    matches_your_search_criteria: 'Matcher dine søgekriterier',
    sort_by: 'Sorter efter',
    compare: 'Sammenlign',
    show_on_map: 'Vis på Kort',
    social_media_share: 'Del på Sociale Medier',
    hotel_description: 'Hotelbeskrivelse',
    included: 'Inkluderet',
    room_price_starting_from: 'Værelsespris Fra',
    filters: 'Filtre',
    price_range: 'Prisinterval',
    star_rating: 'Stjernebedømmelse',
    all: 'Alle',
    view_deal: 'Se Tilbud',
    book_now: 'Book Nu',
    per_night: 'pr. nat',
    total_price: 'Samlet Pris',
    per_person: 'pr. person',
    no_results: 'Ingen resultater fundet. Prøv en anden søgning.',
    back_to_search: 'Tilbage til Søgning',
    searching_for_deals: 'Søger efter de bedste tilbud...',

    // Booking & Payment
    passenger_details: 'Passageroplysninger',
    contact_details: 'Kontaktoplysninger',
    payment_details: 'Betalingsoplysninger',
    first_name: 'Fornavn',
    last_name: 'Efternavn',
    birth_date: 'Fødselsdato',
    gender: 'Køn',
    male: 'Mand',
    female: 'Kvinde',
    email: 'E-mail',
    phone: 'Telefon',
    address: 'Adresse',
    city: 'By',
    zip_code: 'Postnummer',
    country: 'Land',
    card_number: 'Kortnummer',
    card_holder: 'Kortholders Navn',
    expiry_date: 'Udløbsdato',
    cvv: 'CVV/CVC',
    pay_now: 'Betal & Bekræft',
    booking_summary: 'Bookingsoversigt',
    check_in: 'Check-in',
    check_out: 'Check-ud',
    nights: 'Nætter',
    room_type: 'Værelses Type',
    board_basis: 'Forplejning',
    total_amount: 'Samlet Beløb',
    secure_payment: 'Sikker Betaling',
    reservation_success: 'Reservation Gennemført!',
    reservation_number: 'Reservationsnummer',
    thank_you_message: 'Tak for din booking. En bekræftelsesmail er blevet sendt til dig.',
    back_to_home: 'Tilbage til Forsiden',
    processing: 'Behandler...',
    adult: 'Voksen',
    child: 'Barn',
    passenger: 'Passager',
    booking_overview: 'Bookingoversigt',
    complete_booking_msg: 'Udfyld dine oplysninger for at afslutte reservationen',
    back_to_hotel: 'Tilbage til Hotellet',
    lead_passenger: 'Hovedpassager',
    proceed_to_payment: 'Fortsæt til Betaling →',
    price_change: 'Prisændring',
    price_breakdown: 'Prisopdeling',
    all_taxes_included: 'Alle afgifter inkluderet',
    your_trip: 'Din Rejse',
    booking_valid_for: 'Bookingen er gyldig i',
    total_amount_label: 'samlet beløb',
    adults_short: 'voksne',
    children_short: 'børn',
    room: 'værelse',
    flight: 'Fly',
    share_trip: 'Del rejse',
    link_copied: 'Link kopieret!',
    flight_change: 'flyændring',
    show_full_flight_info: 'Vis fuld flyinformation',
    start_new_session: 'Start ny session',
    baggage_selection: 'Bagagevalg',
    extra_baggage: 'Ekstra Bagage',

    // Payment Page
    payment_initializing: 'Initialiserer betalingsgateways...',
    payment_connecting: 'Opretter forbindelse til reservationssystem...',
    payment_fetching_methods: 'Henter betalingsmetoder...',
    payment_confirming: 'Bekræfter booking...',
    payment_processing: 'Behandler betaling...',
    payment_redirecting: 'Omdirigerer til betalingsgateway...',
    payment_redirecting_short: 'Omdirigerer...',
    payment_connection_issue: 'Forbindelsesproblem',
    payment_retry: 'Prøv Igen',
    payment_select_method: 'Vælg Betalingsmetode',
    payment_no_methods: 'Ingen betalingsmetoder fundet. Prøv igen senere.',
    payment_redirect_info: 'Du vil blive omdirigeret til vores sikre betalingspartner for at gennemføre din transaktion sikkert.',
    payment_accept_terms_prefix: 'Jeg accepterer',
    payment_terms_title: 'Vilkår og Betingelser',
    payment_accept_close: 'Accepter & Luk',
    payment_proceed: 'Fortsæt til Sikker Betaling',

    // TravelMate
    view_hotel: 'Se Hotel',
    travelmate_ai: 'Lumi Assist',
    beta: 'Beta',
    your_budget: 'Dit Budget',
    gap: 'Forskel',
    market_avg: 'Markedsgns.',
    est_total: 'Est. Total',

    // Chat / Live Support
    chat_support: 'Live Support',
    chat_ai_assistant: 'AI Assistent',
    chat_agent_active: 'Agent Aktiv',
    chat_waiting_agent: 'Venter på Agent...',
    chat_request_agent: 'Brug for menneskelig hjælp? Anmod om Agent',
    chat_type_message: 'Skriv en besked...',
    chat_listening: 'Lytter...',
    chat_welcome: 'Hej! Jeg er Luna, din virtuelle rejseassistent. Jeg kan tjekke priser, hjælpe dig med at booke eller besvare spørgsmål. Hvordan kan jeg hjælpe dig i dag?',
    chat_agent_connected: 'Forbinder dig til en menneskelig agent...',
    chat_agent_notified: 'Jeg har underrettet en menneskelig agent. De vil være hos dig inden for kort tid. Estimeret ventetid: 2 minutter.',

    // Footer
    copyright: '© 2025 Lumitours. Alle rettigheder forbeholdt.',
    admin_login: 'Admin',

    // Admin
    dashboard: 'Oversigt',
    bookings: 'Bookinger',
    my_bookings: 'Mine Bookinger',
    manage_reservations: 'Administrer dine reservationer og dokumenter',
    content_manager: 'Indholdshåndtering',
    logs: 'Systemlogfiler',
    settings: 'API-indstillinger',
    logout: 'Log ud',
    admin_panel: 'Adminpanel',
    recent_bookings: 'Seneste Bookinger',
    total_revenue: 'Samlet Omsætning',
    active_users: 'Aktive Brugere',
    system_status: 'Systemstatus',
    inspect_product: 'Inspicér Produkt',
    enter_product_id: 'Indtast Produkt-ID (f.eks. 8007)',
    fetch_details: 'Hent Detaljer',
    raw_data: 'Rå Data',
    ai_designer: 'AI Designer',
    live_support: 'Live Support',
    exit_to_app: 'Gå til App',

    // Settings
    api_settings: 'API-indstillinger',
    service_address: 'Tjenesteadresse',
    agency: 'Bureau',
    api_user: 'Bruger',
    api_password: 'Adgangskode',
    save_settings: 'Gem Indstillinger',
    cancel: 'Annuller',
    settings_saved: 'Indstillinger gemt!',
    api_settings_not_configured: 'API-indstillinger er ikke konfigureret. Konfigurer dem først.',
    api_search_error: 'Der opstod en fejl under søgningen. Se konsollen for detaljer.',
    test_connection: 'Test Forbindelse',
    api_test_tool: 'API Test Værktøj',
    test_instructions: 'Klik på knappen for at sende en testanmodning til den konfigurerede tjenesteadresse med de gemte legitimationsoplysninger.',
    testing: 'Tester...',
    test_success: 'Succes! API\'et svarede.',
    test_failed: 'Fejlede. Se svar/fejl nedenfor.',
    cors_error_help: 'En netværksfejl opstod. Dette er ofte et CORS-problem. Sørg for, at serveren på tjenesteadressen tillader anmodninger fra denne oprindelse.',
    test_response: 'API-svar',

    // API Logs
    api_logs: 'API-logfiler',
    request: 'Anmodning',
    response: 'Svar',

    // Flight Options Modal
    flight_options_title: 'Fly Muligheder',
    flight_options_subtitle: 'Se og vælg alternative fly',
    outbound_flight: 'Udrejse',
    inbound_flight: 'Hjemrejse',
    all_airlines: 'Alle Flyselskaber',
    direct_flight: 'Direkte Fly',
    max_1_stop: 'Maks 1 Stop',
    max_2_stops: 'Maks 2 Stop',
    loading_flights: 'Indlæser fly...',
    no_flights_found_criteria: 'Ingen fly fundet der matcher disse kriterier.',
    total_difference: 'Samlet Forskel',
    confirm_flights: 'Bekræft Fly',
    selected: 'Valgt',
    stops: 'Stop',
    any: 'Alle',
    clear_filters: 'Ryd Filtre',
    tour_details: 'Rejsedetaljer',
    alternative_flights: 'Alternative Fly',
    select_this_flight: 'Vælg Dette Fly',
    searching_alternative_flights: 'Søger efter alternative fly...',
    return_flight: 'Retur',
    select_room: 'Vælg Værelse',
    total_price_label: 'Samlet Pris',
    view_flight_options: 'Se Fly Muligheder',
    no_flights_found: 'Ingen fly fundet',
    close: 'Luk',
    confirm: 'Bekræft',
    error_loading_flights: 'Der opstod en fejl under indlæsning af flyinformation',

    // Admin Sub-components
    hotel_manager: 'Hotelhåndtering',
    tour_cache: 'Tour Cache',
    data_explorer: 'Dataudforskare',
    page_builder: 'Sidebygger',
    field_mapper: 'Feltmapper',
    cms_manager: 'CMS Håndtering',

    // Common Admin Actions
    add_new: 'Tilføj Ny',
    edit: 'Rediger',
    delete: 'Slet',
    save: 'Gem',
    refresh: 'Opdater',
    loading: 'Indlæser...',
    no_data: 'Ingen data tilgængelig',
    actions: 'Handlinger',
    status: 'Status',
    name: 'Navn',
    description: 'Beskrivelse',
    created: 'Oprettet',
    updated: 'Opdateret',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    confirm_delete: 'Er du sikker på, at du vil slette dette element?',

    // Hotel Manager
    hotels_list: 'Hotelliste',
    add_hotel: 'Tilføj Hotel',
    edit_hotel: 'Rediger Hotel',
    hotel_name: 'Hotelnavn',
    hotel_location: 'Beliggenhed',
    hotel_rating: 'Bedømmelse',
    hotel_featured: 'Fremhævet',
    sync_hotels: 'Synkroniser Hoteller',

    // Tour Cache
    tour_cache_manager: 'Tour Cache Håndtering',
    queries: 'Søgninger',
    data: 'Data',
    statistics: 'Statistik',
    backups: 'Sikkerhedskopier',
    new_query: 'Ny Søgning',
    query_name: 'Søgenavn',
    departure_point: 'Afrejsested',
    arrival_point: 'Ankomststed',
    check_in_start: 'Check-in Start',
    check_in_end: 'Check-in Slut',
    cache_days: 'Cache Dage',
    schedule: 'Tidsplan',
    automatic_schedule: 'Automatisk Tidsplan',
    work_hour: 'Arbejdstid',
    save_query: 'Gem Søgning',
    saving: 'Gemmer...',
    cancel_edit: 'Annuller',
    no_queries: 'Ingen søgninger fundet.',
    run: 'Kør',
    delete_query_confirm: 'Er du sikker på, at du vil slette denne søgning?',
    total_queries: 'Antal Søgninger',
    scheduled: 'Planlagt',
    stop: 'Stop',
    cache_queries: 'Cache Søgninger',
    add_query: 'Tilføj Søgning',
    run_query: 'Kør Søgning',
    cache_status: 'Cache Status',
    last_run: 'Sidste Kørsel',
    total_results: 'Antal Resultater',

    // Data Explorer
    select_entity: 'Vælg Entitet',
    view_details: 'Se Detaljer',
    export_data: 'Eksporter Data',

    // Page Builder
    pages: 'Sider',
    create_page: 'Opret Side',
    page_title: 'Sidetitel',
    page_slug: 'Side-slug',
    publish: 'Publicer',
    unpublish: 'Afpublicer',

    // Hotel Detail Page
    general_information: 'Generel Information',
    short_description: 'Kort Beskrivelse',
    full_description: 'Fuld Beskrivelse',
    generate_with_ai: 'Generer med AI',
    gallery_media: 'Galleri & Medier',
    manage_media: 'Administrer Alle Medier',
    location_data: 'Lokationsdata',
    latitude: 'Breddegrad',
    longitude: 'Længdegrad',
    sync_from_api: 'Synk. fra Tourvisio',
    discard: 'Kassér',
    save_changes: 'Gem Ændringer',
    published: 'Publiceret',
    draft: 'Kladde',
    last_updated: 'Sidst opdateret',
    last_updated_by: 'af',
    room_mapping: 'Værelsesmapping',
    social_links: 'Sociale Links',
    region: 'Region',
    star_rating_label: 'Stjernebedømmelse',
    max_chars: 'MAKS {0} TEGN',
    click_to_upload: 'Klik for at uploade eller træk og slip',
    upload_formats: 'SVG, PNG, JPG eller GIF (maks. 10MB)',
    map_preview_disabled: 'Kortforhåndsvisning Deaktiveret',
    use_api_data: 'Brug API Data',
    use_manual_data: 'Brug Manuel Data',
    api_data: 'API Data',
    manual_data: 'Manuel Data',
    syncing: 'Synkroniserer...',
    sync_complete: 'Synkronisering Fuldført',
    select_data_source: 'Vælg Datakilde',
    sync_from_tourvisio: 'Synk. fra Tourvisio',
    current_value: 'Nuværende Værdi',
    new_api_value: 'Ny API Værdi',
    confirm_sync: 'Bekræft Synkronisering',
    page_not_found: 'Side Ikke Fundet',
    page_not_found_desc: 'Siden du leder efter eksisterer ikke eller er blevet flyttet.',
    go_to_homepage: 'Gå til Forsiden',
    back_to_list: 'Tilbage til Liste',
    hotel_details: 'Hoteldetaljer',
    rooms: 'Værelser',
    amenities: 'Faciliteter',
    gallery: 'Galleri',

    // Flight Search
    flight_select_btn: 'Vælg Fly',
    searching_flights_status: 'Søger fly...',
    search_failed_msg: 'Søgning mislykkedes',
    retry_action: 'Prøv Igen',
    no_flights_found_msg: 'Ingen fly fundet',
    price_per_person: 'pr. person',
    select_action: 'Vælg',

    // Admin Panel Sidebar
    destinations: 'Destinationer',
    tours_cache: 'Tour Cache',
    reisegiganten_export: 'Reisegiganten',
    media_library: 'Mediebibliotek',
    content: 'Indhold',
    pages_menu: 'Sider',
    data_explorer_menu: 'Dataudforskare',
    ai_manager: 'AI Håndtering',
    users: 'Brugere',
    settings_menu: 'Indstillinger',
    theme_toggle: 'Tema Skift',
    reporting: 'Rapportering',
    ads_management: 'Annonceadministration',
    social_media: 'Sociale Medier',
    api_management: 'API Administration',
    api_diagnostics: 'API Diagnostik',
    seo_tools: 'SEO Værktøjer',
    support_center: 'Support Center',
    mapping: 'Data Mapping',
    data_mapper: 'Data Mapper',
    public_api: 'Offentlig API',
    api_keys: 'API Nøgler',
    cache_management: 'Cache',
    site_management: 'Side Indstillinger',
    contents: 'Indhold',
    tour_assist: 'Tour Assist',
    ai_planner: 'AI Planlægger',
    static_hotels: 'Statiske Hoteller',
    scraped_hotels: 'Scrapede Hoteller',
    hotel_scraper: 'Hotel Scraper',
    enriched_content: 'Beriget Indhold',
    cached_data: 'Cachet Data',
    featured_image: 'Fremhævet Billede',
    featured_image_desc: 'Dette billede vises øverst på detailsiden',
    remove_featured_image: 'Fjern Fremhævet Billede',
    featured_image_removed: 'Fremhævet billede fjernet',
    featured_image_updated: 'Fremhævet billede opdateret',
    fetch_from_website: 'Hent fra Websted',
    scraping: 'Scraper...',
    rescrape: 'Scrape Igen',
    translate_to_english: 'Oversæt til Engelsk',
    translating: 'Oversætter...',
    no_enriched_content: 'Intet beriget indhold tilgængeligt',
    control_panel: 'Kontrolpanel',
    overview_management: 'Oversigt & Håndtering',

    // Field Mapper
    field_mapper_title: 'Feltmapper',
    field_mapper_subtitle: 'Træk og slip API-felter til visningsfelter',
    create_defaults: 'Opret Standarder',
    reset: 'Nulstil',
    new_field: 'Nyt Felt',
    entity_hotels: 'Hoteller',
    entity_rooms: 'Værelser',
    entity_flights: 'Fly',
    entity_offers: 'Tilbud',
    entity_destinations: 'Destinationer',
    format_text: 'Tekst',
    format_number: 'Nummer',
    format_currency: 'Valuta',
    format_date: 'Dato',
    format_datetime: 'Dato/Tid',
    format_image: 'Billede',
    format_rating: 'Bedømmelse',
    format_badge: 'Badge',
    format_list: 'Liste',
    format_html: 'HTML',
    group_basic: 'Grundlæggende Information',
    group_pricing: 'Prissætning',
    group_media: 'Medier',
    group_features: 'Funktioner',
    group_schedule: 'Planlægning',
    group_airports: 'Lufthavne',
    group_dates: 'Datoer',
    group_passengers: 'Passagerer',
    group_details: 'Detaljer',
    mappings_load_failed: 'Kunne ikke indlæse mappinger',
    order_saved: 'Rækkefølge gemt',
    order_save_failed: 'Kunne ikke gemme rækkefølge',
    saved: 'Gemt',
    save_failed: 'Gemning mislykkedes',
    field_added: 'Nyt felt tilføjet',
    fill_all_fields: 'Udfyld venligst alle felter',
    add_failed: 'Tilføjelse mislykkedes',
    deleted: 'Slettet',
    delete_failed: 'Sletning mislykkedes',
    defaults_created: 'Standardfelter oprettet',
    operation_failed: 'Handling mislykkedes',
    reset_complete: 'Nulstilling fuldført',
    no_mappings: 'Ingen mappinger for denne entitet',
    edit_field: 'Rediger Felt',
    display_name: 'Visningsnavn',
    source_field: 'Kildefelt (API)',
    format: 'Format',
    group: 'Gruppe',
    icon: 'Ikon',
    field_key: 'Feltnøgle (unik)',
    add: 'Tilføj',
    system: 'system',
    visible: 'Synlig',
    in_list: 'I Liste',
    in_card: 'I Kort',
    confirm_delete_field: 'Er du sikker på, at du vil slette dette felt?',
    confirm_reset_mappings: 'Er du sikker på, at du vil nulstille mappinger?',

    // Field Labels (for i18n in FieldMapper)
    field_hotel_name: 'Hotelnavn',
    field_stars: 'Stjerner',
    field_location: 'Beliggenhed',
    field_city: 'By',
    field_country: 'Land',
    field_description: 'Beskrivelse',
    field_thumbnail: 'Hovedbillede',
    field_facilities: 'Faciliteter',
    field_room_type: 'Værelsestype',
    field_capacity: 'Kapacitet',
    field_size: 'Størrelse (m²)',
    field_room_amenities: 'Værelsesfaciliteter',
    field_airline: 'Flyselskab',
    field_flight_number: 'Flynummer',
    field_departure_time: 'Afgang',
    field_arrival_time: 'Ankomst',
    field_duration: 'Varighed',
    field_departure_airport: 'Afgangslufthavn',
    field_arrival_airport: 'Ankomstlufthavn',
    field_total_price: 'Samlet Pris',
    field_per_person: 'Pr. Person',
    field_check_in: 'Check-in',
    field_check_out: 'Check-ud',
    field_nights: 'Nætter',
    field_board_type: 'Forplejningstype',
    field_room: 'Værelse',
    field_adults: 'Voksne',
    field_children: 'Børn',
    field_destination: 'Destination',
    field_code: 'Kode',

    // AI Designer
    ai_designer_title: 'Lumi Assist Website Designer',
    ai_designer_history: 'Historik',
    ai_designer_history_title: 'Handlingshistorik',
    ai_designer_clear_all: 'Ryd Alle',
    ai_designer_no_operations: 'Ingen handlinger endnu',
    ai_designer_undo: 'Fortryd',
    ai_designer_undone: 'Fortrydt',
    ai_designer_instruction: 'Fortæl Lumi Assist hvilke ændringer du ønsker. Du kan opdatere forsiden, flash sale bannere eller destinationsdetaljer øjeblikkeligt.',
    ai_designer_placeholder: 'F.eks: \'Opdater forsiden hero til Egypt Winter Sun kampagne\' eller \'Gør flash sale banneret rødt og skriv 50% rabat!\'',
    ai_designer_apply: 'Anvend Ændringer',
    ai_designer_processing: 'Behandler...',
    ai_api_key_missing: 'AI API Nøgle Mangler',
    ai_api_key_instruction: 'AI Designer kræver miljøvariablen VITE_GEMINI_API_KEY for at fungere.',
    ai_api_key_add: 'Tilføj til frontend .env-fil:',
    ai_undo_success: 'Fortrydt:',
    ai_undo_failed: 'Fortryd mislykkedes',
    ai_history_cleared: 'Historik ryddet',
    ai_settings_updated: 'Widget-indstillinger opdateret',
    ai_destination_updated: 'Destination opdateret:',

    // Admin Search
    search_placeholder: 'Søg...',
    search_history: 'Søgehistorik',

    // Additional UI Strings
    uploading: 'Uploader...',
    drop_images_or_click: 'Slip billeder eller klik for at uploade',
    fetch_images: 'Hent Billeder',
    fetch_data: 'Hent Data',
    upload_image: 'Upload Billede',
    chart_loading: 'Diagram Indlæser...',
    downloading: 'Downloader...',

    // Footer
    footer_company: 'Virksomhed',
    footer_about_us: 'Om Os',
    footer_terms: 'Vilkår & Betingelser',
    footer_privacy: 'Privatliv & Sikkerhed',
    footer_cancellation: 'Afbestillingspolitik',
    footer_contact: 'Kontakt',
    footer_newsletter: 'Nyhedsbrev',
    footer_subscribe: 'Tilmeld',
    footer_email_placeholder: 'Din e-mail adresse',
    footer_customer_service: 'Kundeservice',
    footer_travel_line: 'Ring til Os',
    footer_company_desc: 'Premium pakkerejser til håndplukkede destinationer. Vi tilbyder skræddersyede rejseoplevelser med personlig service og kvalitetsgaranti.',

    // SearchWidget Preview
    check_in_label: 'Check-in',
    check_out_label: 'Check-ud',
    duration_label: 'Varighed',
    travelers_label: 'Rejsende',
    rooms_label: 'Værelser',
    ages_18_plus: 'Alder 18+',
    ages_0_17: 'Alder 0-17',
    years_old: 'år',
    select_duration_hint: 'Vælg varighed for at se datoer',

    // Search Results Page UI
    showing_results: 'Viser',
    of_results: 'af',
    loading_more: 'Indlæser flere hoteller...',
    try_adjusting: 'Prøv at justere dine filtre eller søgekriterier.',
    featured_offer: 'Fremhævet Tilbud',
    ai_insights: 'Live AI Indsigter',
    ai_highlight: 'AI Fremhævning',
    ai_analyzing: 'TravelMate AI Analyserer...',

    // Hotel Detail Pricing Summary
    total_price_for_nights: 'SAMLET PRIS FOR {0} NÆTTER',
    nights_count: '{0} Nætter',
    guests_label: 'Gæster',
    adults_count: '{0} Voksne',
    children_count: '{0} Børn',
    package_includes_desc: 'Inkluderer fly, hotel, transfer og afgifter',
    selected_room_label: 'VALGT VÆRELSE',
    select_room_hint: 'Vælg venligst et værelse ovenfor',
    select_room_btn: 'Vælg et Værelse',
    secure_booking_msg: 'Sikker booking • Øjeblikkelig bekræftelse',
    follow_on_instagram: 'Følg på Instagram',

    // Map
    map_unavailable: 'Kortforhåndsvisning ikke tilgængelig',
    alternative_flights_title: 'Alternative flyafgange',
    current_selection_label: 'Nuværende valg',
  },
};

// Translation map for API-sourced filter values (board types, themes, facilities)
// These values come from the Tourvisio API in English and need localization
// Admin can manage these in the constants file
export const FILTER_TRANSLATIONS: Record<string, Record<string, string>> = {
  sv: {
    // Board Types (Hotellpension)
    'Room Only': 'Endast Rum',
    'Bed And Breakfast': 'Frukost',
    'Bed & Breakfast': 'Frukost',
    'No Breakfast': 'Utan Frukost',
    'No Meal': 'Utan Måltid',
    'Breakfast Continental': 'Kontinental Frukost',
    'All Inclusive': 'All Inclusive',
    'Ultra All Inclusive': 'Ultra All Inclusive',
    'All Inclusive Plus': 'Allt Inklusive Plus',
    'Soft All Inclusive': 'Soft Allt Inklusive',
    'Half Board': 'Halvpension',
    'Full Board': 'Helpension',
    'Self Catering': 'Självhushåll',
    'Breakfast Buffet': 'Frukostbuffé',
    'Breakfast American': 'Amerikansk Frukost',

    // Hotel Themes (Hotellteman)
    'Luxury Hotel': 'Lyxhotell',
    'Spa Hotel': 'Spahotell',
    'Family Hotel': 'Familjehotell',
    'City Hotel': 'Stadshotell',
    'Historic Hotel': 'Historiskt Hotell',
    'Beach Hotel': 'Strandhotell',
    'Budget Hotel': 'Budgethotell',
    'Hotel De Charme': 'Boutiquehotell',
    'Conference Hotel': 'Konferenshotell',
    'Boutique Hotel': 'Boutiquehotell',
    'Resort Hotel': 'Resorthotell',
    'Adults Only': 'Endast Vuxna',
    'All Inclusive Hotel': 'Allt Inklusive-hotell',
    'Family-friendly Hotel': 'Familjevänligt Hotell',
    'Club Resort': 'Klubb Resort',
    'Casino Resort': 'Kasinoresort',

    // Hotel Badges / Categories
    'Premium': 'Premium',
    'Luxury': 'Lyx',
    'Premium, Luxury': 'Premium, Lyx',

    // Facilities (Hotellfaciliteter)
    'WiFi': 'WiFi',
    'Free WiFi': 'Gratis WiFi',
    'Pool': 'Pool',
    'Outdoor Pool': 'Utomhuspool',
    'Indoor Pool': 'Inomhuspool',
    'Restaurant': 'Restaurang',
    'Bar': 'Bar',
    'Air Condition': 'Luftkonditionering',
    'Air Conditioning': 'Luftkonditionering',
    'Fitness Center': 'Gym',
    'Fitness': 'Gym',
    'Gym': 'Gym',
    'Room Service': 'Rumsservice',
    'Spa': 'Spa',
    'Sauna': 'Bastu',
    'Kids Club': 'Barnklubb',
    'Beach Access': 'Strandtillgång',
    'Private Beach': 'Privat Strand',
    'Parking': 'Parkering',
    'Free Parking': 'Gratis Parkering',
    'Elevator': 'Hiss',
    'Laundry': 'Tvättservice',
    'Garden': 'Trädgård',
    'Terrace': 'Terrass',
    'Balcony': 'Balkong',
    'Mini Bar': 'Minibar',
    'Safe': 'Kassaskåp',
    'Hairdryer': 'Hårtork',
    'TV': 'TV',
    'Telephone': 'Telefon',
    'Iron': 'Strykjärn',
    'Bathrobe': 'Badrock',
    'Slippers': 'Tofflor',
    'Coffee Machine': 'Kaffemaskin',
    'Kettle': 'Vattenkokare',
    'Concierge': 'Concierge',
    'Business Center': 'Affärscenter',
    'Meeting Room': 'Konferensrum',
    'Shuttle Service': 'Transferservice',
    'Pet Friendly': 'Husdjursvänligt',
    'Wheelchair Accessible': 'Rullstolsanpassat',
    'Non Smoking': 'Rökfritt',
    'Beach': 'Strand',
    'Waterslide': 'Vattenrutschbana',
    'Animation': 'Underhållning',
    'Tennis': 'Tennis',
    'Golf': 'Golf',
    'Diving': 'Dykning',
    'Water Sports': 'Vattensport',
  },
  da: {
    // Board Types (Hotelpension)
    'Room Only': 'Kun Værelse',
    'Bed And Breakfast': 'Morgenmad',
    'Bed & Breakfast': 'Morgenmad',
    'No Breakfast': 'Uden Morgenmad',
    'No Meal': 'Uden Måltid',
    'Breakfast Continental': 'Kontinental Morgenmad',
    'All Inclusive': 'All Inclusive',
    'Ultra All Inclusive': 'Ultra All Inclusive',
    'All Inclusive Plus': 'All Inclusive Plus',
    'Soft All Inclusive': 'Soft All Inclusive',
    'Half Board': 'Halvpension',
    'Full Board': 'Helpension',
    'Self Catering': 'Selvforplejning',
    'Breakfast Buffet': 'Morgenmadsbuffet',
    'Breakfast American': 'Amerikansk Morgenmad',

    // Hotel Themes (Hoteltemaer)
    'Luxury Hotel': 'Luksushotel',
    'Spa Hotel': 'Spahotel',
    'Family Hotel': 'Familiehotel',
    'City Hotel': 'Byhotel',
    'Historic Hotel': 'Historisk Hotel',
    'Beach Hotel': 'Strandhotel',
    'Budget Hotel': 'Budgethotel',
    'Hotel De Charme': 'Boutiquehotel',
    'Conference Hotel': 'Konferencehotel',
    'Boutique Hotel': 'Boutiquehotel',
    'Resort Hotel': 'Resorthotel',
    'Adults Only': 'Kun Voksne',
    'All Inclusive Hotel': 'All Inclusive Hotel',
    'Family-friendly Hotel': 'Familievenligt Hotel',
    'Club Resort': 'Klub Resort',
    'Casino Resort': 'Casino Resort',

    // Hotel Badges / Categories
    'Premium': 'Premium',
    'Luxury': 'Luksus',
    'Premium, Luxury': 'Premium, Luksus',

    // Facilities (Hotelfaciliteter)
    'WiFi': 'WiFi',
    'Free WiFi': 'Gratis WiFi',
    'Pool': 'Pool',
    'Outdoor Pool': 'Udendørs Pool',
    'Indoor Pool': 'Indendørs Pool',
    'Restaurant': 'Restaurant',
    'Bar': 'Bar',
    'Air Condition': 'Aircondition',
    'Air Conditioning': 'Aircondition',
    'Fitness Center': 'Fitnesscenter',
    'Fitness': 'Fitness',
    'Gym': 'Fitnesscenter',
    'Room Service': 'Roomservice',
    'Spa': 'Spa',
    'Sauna': 'Sauna',
    'Kids Club': 'Børneklub',
    'Beach Access': 'Strandadgang',
    'Private Beach': 'Privat Strand',
    'Parking': 'Parkering',
    'Free Parking': 'Gratis Parkering',
    'Elevator': 'Elevator',
    'Laundry': 'Vaskeservice',
    'Garden': 'Have',
    'Terrace': 'Terrasse',
    'Balcony': 'Altan',
    'Mini Bar': 'Minibar',
    'Safe': 'Pengeskab',
    'Hairdryer': 'Hårtørrer',
    'TV': 'TV',
    'Telephone': 'Telefon',
    'Iron': 'Strygejern',
    'Bathrobe': 'Badekåbe',
    'Slippers': 'Hjemmesko',
    'Coffee Machine': 'Kaffemaskine',
    'Kettle': 'Elkedel',
    'Concierge': 'Concierge',
    'Business Center': 'Forretningscenter',
    'Meeting Room': 'Mødelokale',
    'Shuttle Service': 'Shuttleservice',
    'Pet Friendly': 'Dyrevenligt',
    'Wheelchair Accessible': 'Kørestolsvenligt',
    'Non Smoking': 'Ikke-ryger',
    'Beach': 'Strand',
    'Waterslide': 'Vandrutsjebane',
    'Animation': 'Underholdning',
    'Tennis': 'Tennis',
    'Golf': 'Golf',
    'Diving': 'Dykning',
    'Water Sports': 'Vandsport',
  },
};

// Helper function to translate API filter values
export const translateFilterValue = (value: string, language: string): string => {
  if (language === 'en' || !value) return value;
  const translations = FILTER_TRANSLATIONS[language];
  if (!translations) return value;

  // Try exact match first
  if (translations[value]) return translations[value];

  // Try case-insensitive match (Title Case normalization)
  const titleCase = value.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  if (translations[titleCase]) return translations[titleCase];

  return value;
};
